import { shiftDate, localDate } from "./dates.js";

// Whether a TV/anime season has actually aired yet, derived from the local
// catalog. Seasons used to be known only by release YEAR, so a season premiering
// later in the current year looked identical to one already out — which made the
// app auto-mark it seen, inherit a rating onto it, and miss it from Incoming.
// These helpers give a sub-year answer from the best data on hand.

// Best-known premiere date for a season, as an ISO "YYYY-MM-DD" string (or null):
//   1. the precise `premiere_date` from the catalog (worker now provides it),
//   2. else the earliest episode airdate for that season,
//   3. else the start of the premiere year (coarse fallback),
//   4. else null (truly unknown).
export function seasonPremiereDate(season, episodes = []) {
	if (season?.premiere_date) return season.premiere_date;

	let earliest = null;
	for (const e of episodes) {
		if (e.season !== season?.number) continue;
		if (!e.airdate) continue;
		if (earliest == null || e.airdate < earliest) earliest = e.airdate;
	}
	if (earliest) return earliest;

	if (season?.premiere_year) return `${season.premiere_year}-01-01`;
	return null;
}

// Has this season aired as of `today` ("YYYY-MM-DD")? An unknown premiere date is
// treated as released, so seasons with no date info behave exactly as before.
export function seasonReleased(season, episodes, today) {
	const d = seasonPremiereDate(season, episodes ?? []);
	if (d == null) return true;
	return d <= today;
}

// How long a cached TV/anime catalog is trusted before it's re-fetched (by the
// detail views on open, and by the daily catalog pass in db/catalog-refresh.js).
// Premiere dates shift and running shows grow new seasons; a week keeps things
// honest without hammering TVmaze — except for a show that's mid-run (an episode
// within AIRING_WINDOW_DAYS either side of today), whose schedule can change
// week to week: that one is trusted for a single day.
export const CATALOG_MAX_AGE_DAYS = 7;
export const AIRING_CATALOG_MAX_AGE_DAYS = 1;
export const AIRING_WINDOW_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

// Is this show mid-run? True when any episode airs within ±windowDays of today.
export function isAiringCatalog(catalog, today, windowDays = AIRING_WINDOW_DAYS) {
	if (!catalog || !today) return false;
	const lo = shiftDate(today, -windowDays);
	const hi = shiftDate(today, windowDays);
	return (catalog.episodes ?? []).some(
		(e) => e?.airdate && e.airdate >= lo && e.airdate <= hi,
	);
}

// A series TVmaze marks "Ended" rarely changes; re-check it every two months.
export const ENDED_CATALOG_MAX_AGE_DAYS = 60;

export function catalogMaxAgeDays(catalog, today) {
	if (isAiringCatalog(catalog, today)) return AIRING_CATALOG_MAX_AGE_DAYS;
	if (catalog?.status === "Ended") return ENDED_CATALOG_MAX_AGE_DAYS;
	return CATALOG_MAX_AGE_DAYS;
}

// A cached catalog is "stale" — due for a re-fetch — when:
//   - it has seasons but none carry `premiere_date` (fetched before the worker
//     started returning that field; unaired-season handling needs it), or
//   - it was fetched more than catalogMaxAgeDays ago, or we can't tell when
//     (no `fetched_at`).
// `now` is a ms timestamp (injectable for tests).
export function isCatalogStale(catalog, now = Date.now()) {
	const seasons = catalog?.seasons ?? [];
	if (seasons.length > 0 && seasons.every((s) => !("premiere_date" in s))) {
		return true;
	}
	const fetched = catalog?.fetched_at ? Date.parse(catalog.fetched_at) : NaN;
	if (Number.isNaN(fetched)) return true;
	const maxAge = catalogMaxAgeDays(catalog, localDate(now));
	return now - fetched > maxAge * DAY_MS;
}

// When a series stops being "Watched" (done → anything else), the seen-state its
// released seasons inherited would silently vanish — and "Continue watching"
// would point at S1E1. Return a season_overrides map with `seen: true` pinned onto
// every released season that has no explicit seen flag, so what you watched
// survives the status change. Explicit flags (either way) are left alone; no
// catalog → unchanged.
export function pinInheritedSeen(seasonOverrides, catalog, today) {
	const next = { ...(seasonOverrides ?? {}) };
	const episodes = catalog?.episodes ?? [];
	for (const s of catalog?.seasons ?? []) {
		if (s?.number == null) continue;
		if (!seasonReleased(s, episodes, today)) continue;
		const key = String(s.number);
		if (next[key]?.seen != null) continue;
		next[key] = { ...(next[key] ?? {}), seen: true };
	}
	return next;
}
